import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import Icon from '../../../components/AppIcon';

const EnergyConsumptionChart = () => {
  const [chartType, setChartType] = useState('consumption');

  const energyData = [
    { 
      month: 'Jan', 
      totalConsumption: 2450, 
      renewable: 695, 
      grid: 1755,
      efficiency: 85.2,
      cost: 245000,
      seasonalFactor: 1.15
    },
    { 
      month: 'Feb', 
      totalConsumption: 2280, 
      renewable: 684, 
      grid: 1596,
      efficiency: 87.1,
      cost: 228000,
      seasonalFactor: 1.08
    },
    { 
      month: 'Mar', 
      totalConsumption: 2150, 
      renewable: 731, 
      grid: 1419,
      efficiency: 89.3,
      cost: 215000,
      seasonalFactor: 1.02
    },
    { 
      month: 'Apr', 
      totalConsumption: 1980, 
      renewable: 792, 
      grid: 1188,
      efficiency: 91.7,
      cost: 198000,
      seasonalFactor: 0.95
    },
    { 
      month: 'May', 
      totalConsumption: 1850, 
      renewable: 851, 
      grid: 999,
      efficiency: 93.2,
      cost: 185000,
      seasonalFactor: 0.89
    },
    { 
      month: 'Jun', 
      totalConsumption: 1720, 
      renewable: 895, 
      grid: 825,
      efficiency: 94.8,
      cost: 172000,
      seasonalFactor: 0.85
    },
    { 
      month: 'Jul', 
      totalConsumption: 1680, 
      renewable: 924, 
      grid: 756,
      efficiency: 95.1,
      cost: 168000,
      seasonalFactor: 0.83
    },
    { 
      month: 'Aug', 
      totalConsumption: 1650, 
      renewable: 941, 
      grid: 709,
      efficiency: 95.6,
      cost: 165000,
      seasonalFactor: 0.82
    },
    { 
      month: 'Sep', 
      totalConsumption: 1720, 
      renewable: 918, 
      grid: 802,
      efficiency: 94.9,
      cost: 172000,
      seasonalFactor: 0.86
    },
    { 
      month: 'Oct', 
      totalConsumption: 1890, 
      renewable: 869, 
      grid: 1021,
      efficiency: 92.4,
      cost: 189000,
      seasonalFactor: 0.92
    },
    { 
      month: 'Nov', 
      totalConsumption: 2080, 
      renewable: 812, 
      grid: 1268,
      efficiency: 89.8,
      cost: 208000,
      seasonalFactor: 1.01
    },
    { 
      month: 'Dec', 
      totalConsumption: 2340, 
      renewable: 748, 
      grid: 1592,
      efficiency: 86.5,
      cost: 234000,
      seasonalFactor: 1.12
    }
  ];

  const chartTypes = [
    { value: 'consumption', label: 'Energy Consumption', icon: 'Zap' },
    { value: 'renewable', label: 'Renewable Mix', icon: 'Sun' },
    { value: 'efficiency', label: 'Efficiency Trends', icon: 'TrendingUp' },
    { value: 'cost', label: 'Cost Analysis', icon: 'DollarSign' }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-popover border border-border rounded-lg p-4 shadow-lg">
          <p className="font-medium text-foreground mb-2">{label} 2024</p>
          {chartType === 'consumption' && (
            <>
              <div className="space-y-1 text-sm">
                <div className="flex items-center justify-between space-x-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full bg-primary"></div>
                    <span className="text-muted-foreground">Total:</span>
                  </div>
                  <span className="font-medium text-foreground">{data?.totalConsumption} MWh</span>
                </div>
                <div className="flex items-center justify-between space-x-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full bg-success"></div>
                    <span className="text-muted-foreground">Renewable:</span>
                  </div>
                  <span className="font-medium text-foreground">{data?.renewable} MWh</span>
                </div>
                <div className="flex items-center justify-between space-x-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full bg-warning"></div>
                    <span className="text-muted-foreground">Grid:</span>
                  </div>
                  <span className="font-medium text-foreground">{data?.grid} MWh</span>
                </div>
              </div>
              <div className="border-t border-border mt-2 pt-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Renewable %:</span>
                  <span className="font-medium text-foreground">
                    {((data?.renewable / data?.totalConsumption) * 100)?.toFixed(1)}%
                  </span>
                </div>
              </div>
            </>
          )}
          {chartType === 'efficiency' && (
            <div className="flex items-center justify-between space-x-4 text-sm">
              <span className="text-muted-foreground">Efficiency:</span>
              <span className="font-medium text-foreground">{data?.efficiency}%</span>
            </div>
          )}
          {chartType === 'cost' && (
            <div className="flex items-center justify-between space-x-4 text-sm">
              <span className="text-muted-foreground">Cost:</span>
              <span className="font-medium text-foreground">${data?.cost?.toLocaleString()}</span>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  const renderChart = () => {
    switch (chartType) {
      case 'consumption':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={energyData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="month" stroke="var(--color-muted-foreground)" fontSize={12} />
              <YAxis stroke="var(--color-muted-foreground)" fontSize={12} label={{ value: 'MWh', angle: -90, position: 'insideLeft' }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="renewable" stackId="a" fill="#10B981" name="Renewable Energy" />
              <Bar dataKey="grid" stackId="a" fill="#F59E0B" name="Grid Energy" />
            </BarChart>
          </ResponsiveContainer>
        );
      
      case 'renewable':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={energyData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="month" stroke="var(--color-muted-foreground)" fontSize={12} />
              <YAxis stroke="var(--color-muted-foreground)" fontSize={12} label={{ value: '%', angle: -90, position: 'insideLeft' }} />
              <Tooltip content={<CustomTooltip />} />
              <Line 
                type="monotone" 
                dataKey={(data) => (data?.renewable / data?.totalConsumption * 100)?.toFixed(1)} 
                stroke="#10B981" 
                strokeWidth={3}
                dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
                name="Renewable Percentage"
              />
            </LineChart>
          </ResponsiveContainer>
        );
      
      case 'efficiency':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={energyData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="month" stroke="var(--color-muted-foreground)" fontSize={12} />
              <YAxis stroke="var(--color-muted-foreground)" fontSize={12} label={{ value: '%', angle: -90, position: 'insideLeft' }} />
              <Tooltip content={<CustomTooltip />} />
              <Line 
                type="monotone" 
                dataKey="efficiency" 
                stroke="#2563EB" 
                strokeWidth={3}
                dot={{ fill: '#2563EB', strokeWidth: 2, r: 4 }}
                name="Energy Efficiency"
              />
            </LineChart>
          </ResponsiveContainer>
        );
      
      case 'cost':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={energyData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="month" stroke="var(--color-muted-foreground)" fontSize={12} />
              <YAxis stroke="var(--color-muted-foreground)" fontSize={12} label={{ value: 'USD', angle: -90, position: 'insideLeft' }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="cost" fill="#0EA5E9" name="Energy Cost" />
            </BarChart>
          </ResponsiveContainer>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <div className="flex items-center space-x-3 mb-4 lg:mb-0">
          <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg">
            <Icon name="Zap" size={20} color="var(--color-accent)" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Energy Consumption & Efficiency</h3>
            <p className="text-sm text-muted-foreground">Monthly energy usage with renewable mix analysis</p>
          </div>
        </div>

        {/* Chart Type Selector */}
        <div className="flex items-center space-x-1 bg-muted rounded-lg p-1">
          {chartTypes?.map((type) => (
            <button
              key={type?.value}
              onClick={() => setChartType(type?.value)}
              className={`flex items-center space-x-2 px-3 py-2 text-xs font-medium rounded-md transition-colors ${
                chartType === type?.value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={type?.icon} size={14} />
              <span className="hidden sm:inline">{type?.label}</span>
            </button>
          ))}
        </div>
      </div>
      {/* Chart */}
      <div className="h-80 mb-6">
        {renderChart()}
      </div>
      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 pt-6 border-t border-border">
        <div className="text-center">
          <p className="text-2xl font-bold text-foreground">23,790</p>
          <p className="text-sm text-muted-foreground">Total MWh (YTD)</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-success">28.4%</p>
          <p className="text-sm text-muted-foreground">Renewable Mix</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-primary">91.2%</p>
          <p className="text-sm text-muted-foreground">Avg Efficiency</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-accent">$2.37M</p>
          <p className="text-sm text-muted-foreground">Total Cost (YTD)</p>
        </div>
      </div>
    </div>
  );
};

export default EnergyConsumptionChart;